import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useFeature } from '../../../hooks/useFeature';
import { useCreatorsList } from '../../../hooks/useCreatorsList';
import Sidebar from '../../admin-dashboard/components/Sidebar';

/**
 * Manager V2 Risk — Créateurs à risque (vue cleartext Phase 1).
 * Route : /manager/risk + /manager/v2/risk.
 * Feature : pages.manager.risk (OFF par défaut — scraping enrichit Phase 2).
 *
 * Phase 1 (cleartext, no scraping) : signaux de risque dérivables sans scraping
 *  - status inactif/suspendu
 *  - dernier live ancien (> 7 jours)
 * Phase 2 (scraping) : violations actives, bans, quit signals — enrichiront
 * cette vue quand `pages.violations.admin` / quit_monitor seront branchés.
 */
const DAYS_THRESHOLD = 7;

const ManagerV2Risk = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName } = useTenant();
  const violationsFeature = useFeature('pages.violations.admin');
  const [collapsed, setCollapsed] = useState(false);
  const { rows, loading, error, reload } = useCreatorsList({
    managerId: userProfile?.id || null,
    includeDeparted: false,
  });

  const atRisk = useMemo(() => {
    const now = Date.now();
    const out = [];
    for (const c of rows) {
      const reasons = [];
      if (c.status === 'suspended') reasons.push({ key: 'suspended', sev: 'high' });
      else if (c.status === 'inactive') reasons.push({ key: 'inactive', sev: 'med' });
      if (c.last_live_date) {
        const days = Math.floor((now - new Date(c.last_live_date).getTime()) / 86400000);
        if (days > DAYS_THRESHOLD) reasons.push({ key: 'staleLive', sev: 'med', days });
      } else {
        reasons.push({ key: 'neverLive', sev: 'med' });
      }
      if (reasons.length > 0) out.push({ ...c, reasons });
    }
    // High severity first
    return out.sort((a, b) => {
      const sa = a.reasons.some((r) => r.sev === 'high') ? 0 : 1;
      const sb = b.reasons.some((r) => r.sev === 'high') ? 0 : 1;
      return sa - sb;
    });
  }, [rows]);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
              {t('riskV2.title', 'Risk')}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">{tenantName} · {atRisk.length} {t('riskV2.atRisk', 'at risk')}</p>
          </div>
        </header>
        <div className="p-4 md:p-6 space-y-4">
          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between">
              <span>{String(error.message || error)}</span>
              <button type="button" onClick={reload} className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium">
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}

          {!violationsFeature && (
            <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4 text-xs text-muted-foreground">
              <span className="font-semibold text-primary mr-1">{t('riskV2.phase2Label', 'Phase 2:')}</span>
              {t('riskV2.phase2Note', 'Violations and quit signals (scraping) will enrich this view once those features are enabled. For now, risk is derived from status and last-live signals only.')}
            </div>
          )}

          {loading ? (
            <div className="rounded-2xl bg-card border border-border h-48 skeleton" />
          ) : atRisk.length === 0 ? (
            <div className="rounded-2xl bg-success/5 border border-success/20 p-8 text-center text-sm text-success">
              {t('riskV2.allClear', 'No at-risk creators right now. 🎉')}
            </div>
          ) : (
            <section className="rounded-2xl bg-card border border-border overflow-hidden">
              <ul className="divide-y divide-border">
                {atRisk.map((c) => {
                  const high = c.reasons.some((r) => r.sev === 'high');
                  return (
                    <li key={c.id} className="px-4 md:px-5 py-3">
                      <Link to={`/manager/team/${c.id}`} className="flex items-start justify-between gap-3 hover:text-primary transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-foreground truncate">{c.display_name || c.tiktok_username || '—'}</div>
                          <div className="text-xs text-muted-foreground truncate">{c.server || '—'} · T{c.tier_current ?? c.tier ?? '—'}</div>
                          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                            {c.reasons.map((r, i) => (
                              <span
                                key={i}
                                className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ${
                                  r.sev === 'high' ? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'
                                }`}
                              >
                                {t(`riskV2.reason.${r.key}`, r.key)}{r.days ? ` (${r.days}d)` : ''}
                              </span>
                            ))}
                          </div>
                        </div>
                        <span className={`shrink-0 w-2.5 h-2.5 rounded-full mt-1 ${high ? 'bg-error' : 'bg-warning'}`} />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default ManagerV2Risk;
